import { Component, createSignal, For, createEffect } from 'solid-js';
import CountryFlag from '../ui/CountryFlag';
import NetworkLogo from '../ui/NetworkLogo';
import PlatformIcon, { getPlatformColor } from '../ui/PlatformIcon';
import { getRealtimeClicksSignal, type RealtimeClick } from '../../stores/realtimeClicksStore';
import { socketState } from '../../services/socket';
import { TbClick, TbChevronUp, TbChevronDown } from 'solid-icons/tb';

interface RealtimeClicksPanelProps {
  onClickSelect?: (click: RealtimeClick) => void;
  maxClicks?: number;
  shouldMinimize?: boolean;
  savedExpandedState?: () => boolean;
}

const RealtimeClicksPanel: Component<RealtimeClicksPanelProps> = (props) => {
  const [isExpanded, setIsExpanded] = createSignal(true);
  const [shouldAnimate, setShouldAnimate] = createSignal(false);

  // Auto-minimize when shouldMinimize is true, restore saved state otherwise
  createEffect(() => {
    // Add animation flag when state changes due to shouldMinimize
    setShouldAnimate(true);

    if (props.shouldMinimize) {
      setIsExpanded(false);
    } else if (props.savedExpandedState) {
      setIsExpanded(props.savedExpandedState());
    }
  });
  // Use global store signal that persists across page navigation
  // Socket listeners are now registered globally in Layout component
  const realtimeClicks = getRealtimeClicksSignal();

  // Get icon based on traffic source (platform)
  const getTrafficSourceIcon = (click: RealtimeClick) => {
    return (
      <PlatformIcon
        platform={click.traffic_source}
        size={16}
        class={`${getPlatformColor(click.traffic_source)}`}
      />
    );
  };

  const handleClickSelect = (click: RealtimeClick) => {
    if (props.onClickSelect) {
      props.onClickSelect(click);
    }
  };

  return (
    <div class="flex flex-col h-full bg-body dark:bg-dark overflow-hidden">
      {/* Header - Sticky */}
      <div class="sticky top-0 z-10 flex items-center justify-between px-4 md:px-5 min-h-16 bg-body dark:bg-dark border-b border-dashed border-gray-200 dark:border-gray-700">
        <h2 class={`text-lg font-medium uppercase flex items-center gap-2 flex-1 tracking-wider ${
          socketState.isConnected()
            ? 'text-gray-900 dark:text-white'
            : 'text-gray-900 dark:text-gray-400'
        }`}>
          <div class="flex items-center gap-1.5">
            <TbClick size={20} class={`${
              socketState.isConnected()
                ? 'text-orange-500 animate-pulse'
                : 'text-gray-400 dark:text-gray-500'
            }`} />
            <span>Real-time Clicks</span>
          </div>
        </h2>
        <button
          onClick={() => {
            setShouldAnimate(true);
            setIsExpanded(!isExpanded());
          }}
          class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-orange-500 transition text-gray-500 dark:text-gray-400 flex-shrink-0"
          title={isExpanded() ? 'Collapse' : 'Expand'}
        >
          {isExpanded() ? <TbChevronUp size={20} /> : <TbChevronDown size={20} />}
        </button>
      </div>


      {/* Content - Scrollable with animation */}
      <div
        class={`flex-1 overflow-hidden flex flex-col ${
          shouldAnimate()
            ? isExpanded()
              ? 'animate-expand-panel-slow'
              : 'animate-collapse-panel-slow'
            : isExpanded()
              ? ''
              : 'hidden'
        }`}
      >
        {(isExpanded() || (shouldAnimate() && !isExpanded())) && (
        <div class="overflow-y-auto p-4 md:p-6 realtime-scrollbar h-full flex-1">
          {realtimeClicks().length === 0 ? (
            <div class="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              <p>Waiting for clicks...</p>
            </div>
          ) : (
            <div class="space-y-2">
              <For each={realtimeClicks()}>
                {(click) => (
                  <button
                    onClick={() => handleClickSelect(click)}
                    class="w-full text-left flex items-center gap-2 p-2 bg-white dark:bg-light-dark rounded-lg hover:bg-[#F3F4F6] dark:hover:bg-gray-700 transition cursor-pointer animate-fadeIn shadow-card"
                  >
                    {/* Country Flag */}
                    <CountryFlag code={click.country || 'xx'} size={16} />
                    {/* User ID */}
                    <p class="font-mono text-xs font-semibold text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">
                      {click.user}
                    </p>
                    {/* Traffic Source Icon */}
                    <div class="flex-shrink-0">
                      {getTrafficSourceIcon(click)}
                    </div>
                    {/* Network Logo */}
                    <div class="flex-shrink-0">
                      <NetworkLogo network={click.network} size={16} />
                    </div>
                  </button>
                )}
              </For>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeClicksPanel;
